// RegionDominance — the §3.4 dominance/containment machinery, shared by the MATERIAL bridge
// (`collectMaterialRegions`) and the SCENERY bridge (`collectSceneryRegions`). GENERIC + PURE:
// takes the structural subset both bridges share (id, polylines, bbox, mode, an optional explicit
// dominance) and resolves each region's dominance; each bridge then stores the result on its own
// region type (the one field that legitimately shadows the authored feature — deriving it needs
// the whole region set, as a per-feature value it cannot exist until the bridge runs).
//
// Semantics (Set12 §3.4, reused verbatim for scenery):
//   - EXPLICIT dominance (authored on the feature) always wins.
//   - Otherwise DERIVED: containment depth among interior-bearing regions (mode != "stroke")
//     — how many OTHER interior-bearing regions strictly contain this one (the vertex test).
//   - STROKES (no interior): maxFillDepth + 1 — "above all fills" (a road crossing a field sits
//     on top; overridable via the explicit field).
// Cycles are impossible (containment is a partial order).
import type {Pt} from "../../Heights/Poisson/@Shared/ConstraintRasterizer/RasterFeatures_Pure.js";
import {pointInPolygon} from "../../Heights/Poisson/@Shared/ConstraintRasterizer.js";
import type {Rect} from "../../../Utils/ThreeJS/VectorStructs.js";

/** The structural dominance input — one region's bridge-resolved geometry + its authored mode
 *  and optional explicit dominance. Both `MaterialRegion` and `SceneryRegion` map onto this. */
export interface RegionDominanceInput {
	id: string;
	polylines: Pt[][];
	/** The bridge's broad-phase bbox (inflated is fine — it is only a conservative pre-filter). */
	bbox: Rect;
	/** The authored mode — "stroke" regions take the above-all-fills default. */
	mode: string;
	/** The feature's authored `dominance`, if set. `null`/`undefined` ⇒ derive. */
	explicitDominance?: number|n;
}

/** Resolve the dominance of EVERY region (the batch form — `maxFillDepth` is computed once):
 *  explicit `explicitDominance` wins; else containment depth for interior-bearing regions, or
 *  `maxFillDepth + 1` for strokes. Returns the resolved values INDEX-ALIGNED with the input. */
export function resolveRegionDominances(regions: readonly RegionDominanceInput[]): number[] {
	const interior = regions.filter(r=>r.mode != "stroke");
	const depthOf = new Map<RegionDominanceInput, number>();
	for (const r of interior) depthOf.set(r, regionContainmentDepth_Gen(r, interior));
	let maxFillDepth = 0;
	for (const d of depthOf.values()) if (d > maxFillDepth) maxFillDepth = d;
	const out: number[] = [];
	for (const r of regions) {
		if (r.explicitDominance != null) out.push(r.explicitDominance);
		else if (r.mode == "stroke") out.push(maxFillDepth + 1);
		else out.push(depthOf.get(r) ?? 0);
	}
	return out;
}

/** The §3.4 containment depth of `inner` among the interior-bearing regions: how many STRICTLY contain it (the vertex test — the DERIVED dominance of a fill). */
export function regionContainmentDepth_Gen(inner: RegionDominanceInput, interiorRegions: readonly RegionDominanceInput[]): number {
	let depth = 0;
	for (const s of interiorRegions) {
		if (s === inner) continue;
		if (!bboxContainsRegion_Gen(s.bbox, inner)) continue; // bbox pre-filter (conservative)
		if (regionStrictlyInside_Gen(inner, s)) depth++;
	}
	return depth;
}

/** Is EVERY vertex of every polyline of `inner` STRICTLY inside `outer`? All-vertices-inside ⇒ the whole boundary (and thus the interior minus nested holes) is contained — the §3.4 partial order. */
function regionStrictlyInside_Gen(inner: RegionDominanceInput, outer: RegionDominanceInput): boolean {
	for (const poly of inner.polylines) {
		for (const p of poly) {
			// Strictly inside: NOT on the boundary (pointInPolygon is inclusive-ish; a vertex
			// shared between touching rings counts as NOT strictly contained — deterministic).
			if (!outer.polylines.some(op=>pointInPolygon(p.x, p.y, op))) return false;
		}
	}
	return true;
}

/** bbox-of-`inner` fully inside bbox-of-`outer`?  Pre-filter only — a false positive is fine (the vertex test decides). */
function bboxContainsRegion_Gen(outerB: Rect, inner: RegionDominanceInput): boolean {
	let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
	for (const poly of inner.polylines) {
		for (const p of poly) {
			if (p.x < minX) minX = p.x;
			if (p.y < minY) minY = p.y;
			if (p.x > maxX) maxX = p.x;
			if (p.y > maxY) maxY = p.y;
		}
	}
	return minX > outerB.x && maxX < outerB.x + outerB.width && minY > outerB.y && maxY < outerB.y + outerB.height;
}
